import { createSlice } from '@reduxjs/toolkit';

// Local storage key
const STORAGE_KEY = 'dashboard_preferences';

// Default state
const initialState = {
    layout: [], // Array of group IDs in order
    hidden: [], // Array of group IDs that are hidden
    _initialized: false // To know if we loaded from storage
};

// Helper to load from storage
const loadFromStorage = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (e) {
        console.error('Failed to load dashboard preferences', e);
    }
    return null;
};

// Helper to save to storage
const saveToStorage = (state) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
            layout: state.layout,
            hidden: state.hidden
        }));
    } catch (e) {
        console.error('Failed to save dashboard preferences', e);
    }
};

const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState,
    reducers: {
        // Initialize with default or stored values
        initializeDashboard: (state, action) => {
            const { defaultLayout } = action.payload; // expect array of IDs
            const stored = loadFromStorage();

            if (stored) {
                state.layout = stored.layout || defaultLayout;
                state.hidden = stored.hidden || [];
            } else {
                state.layout = defaultLayout;
                state.hidden = [];
            }
            state._initialized = true;
        },
        updateLayout: (state, action) => {
            state.layout = action.payload;
            saveToStorage(state);
        },
        toggleVisibility: (state, action) => {
            const id = action.payload;
            if (state.hidden.includes(id)) {
                state.hidden = state.hidden.filter(item => item !== id);
            } else {
                state.hidden.push(id);
            }
            saveToStorage(state);
        },
        resetPreferences: (state, action) => {
            const { defaultLayout } = action.payload;
            state.layout = defaultLayout;
            state.hidden = [];
            saveToStorage(state);
        }
    }
});

export const { initializeDashboard, updateLayout, toggleVisibility, resetPreferences } = dashboardSlice.actions;

// Selectors
export const selectDashboardLayout = (state) => state.dashboard.layout;
export const selectDashboardHidden = (state) => state.dashboard.hidden;
export const selectDashboardInitialized = (state) => state.dashboard._initialized;

export default dashboardSlice.reducer;
